import React, { useEffect, useState } from "react";
import { SUBSCRIPTION } from "../../../../interfaces/subscription";
import { connect } from "react-redux";
import { Col, Modal, ModalBody, ModalHeader, Row, Table } from "reactstrap";
import { withRouter, RouteComponentProps } from "react-router-dom";
import SubscriptionCheckoutModal from "../../../../components/checkout/subscriptionModal";
import { IApplicationState } from "../../../../redux/reducers";
import { X } from "react-feather";
import InvoiceTotal from "../components/invoiceTotal";
import { IUser } from "../../../../interfaces/user";
import { toCamelCase } from "../../../../utils/string";
import Upgrade from "../../../../components/common/upgrade";
import { availableSubscriptionPlans } from "../../../../utils/constants";
import { BillingPeriod } from "./components";
import paymentService from "../../../../services/paymentService";
import Stripe from "@stripe/stripe-js";
import { currencyFormatCentsToDollars } from "../../../../utils/currency";
import Loader from "../../../../components/loader";
import { getSubscriptionPrice, isEmptyObject, isSubscriptionExpired } from "../../../../utils/subscription";
import UsageCreditsCard from "../../../../components/common/usage/UsageCreditsCard";
import CreditsBanner from "../../../../components/common/usage/CreditsBanner";
import userService from "../../../../services/userService";
import { IUserCredits, IUserAccessPermissions } from "../../../../interfaces/user";

export interface ISubscriptionDetails {
	subscription: any;
	latest_invoice: any;
}

interface IProps extends RouteComponentProps {
	active: SUBSCRIPTION;
	profile?: IUser | undefined;
	credits?: IUserCredits;
	permissions?: IUserAccessPermissions;
	loadingCredits: boolean;
	loadingPermissions: boolean;
	getUserCredits: () => any;
	getUserAccessPermissions: () => any;
}

const Subscription: React.FC<IProps> = (props) => {
	const [checkoutModal, setCheckoutModal] = useState<boolean>(false);
	const [selectedPlan, setSelectedPlan] = useState<availableSubscriptionPlans>();
	const [subscriptionType, setSubscriptionType] = useState<string>("");
	const [subscriptionDetails, setSubscriptionDetails] = useState<any>();
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		setLoading(true);
		Promise.all([
			paymentService.getCompanySubscriptionDetails(),
			props.getUserCredits(),
			props.getUserAccessPermissions()
		]).then(([subDetails]) => {
			setSubscriptionDetails(subDetails);
		}).finally(() => {
			setLoading(false);
		});
	}, []);

	useEffect(() => {
		if (props?.profile?.company?.subscription_type) {
			setSubscriptionType(props.profile.company.subscription_type);
		} else if (props.permissions?.subscription_type) {
			setSubscriptionType(props.permissions.subscription_type);
		}
	}, [props.profile, props.permissions]);

	const calculateDaysLeft = (endDate?: string) => {
		if (!endDate) return undefined;
		const end = new Date(endDate);
		const now = new Date();
		const diffTime = end.getTime() - now.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays > 0 ? diffDays : 0;
	};

	const getSubscriptionDescription = () => {
		const status = subscriptionDetails.subscription?.status;
		return `Monthly charge for ${subscriptionType} tier platform access ${status ? "(" + status + ")" : ""
			}`;
	};

	const getPrice = () => {
		return currencyFormatCentsToDollars(subscriptionDetails?.latest_invoice?.subtotal || 0, "USD");
	};


	return (
		<>
			{" "}
			{loading || !subscriptionDetails ? (
				<Loader isLoading={loading} fullScreen />
			) : (
				<>
					<Col className="subscription invoices">
						<Row className={"mt-3 mb-3"}>
							<BillingPeriod subscriptionDetails={getSubscriptionPrice(subscriptionDetails)} />
						</Row>

						{/* Usage & Credits Section */}
						<Row className="mt-4 mb-4">
							<UsageCreditsCard
								credits={props.credits}
								loading={props.loadingCredits}
								isTrial={props.permissions?.is_trial}
								daysLeft={calculateDaysLeft(props.credits?.billing_cycle_end)}
								onUpgradeClick={() => props.history.push("/app/subscribe")}
								onViewDetailsClick={() => props.history.push("/app/subscribe")}
							/>
						</Row>

						{/* Upgrade Banner */}
						<Row className="mt-2 mb-5">
							<CreditsBanner onUpgradeClick={() => props.history.push("/app/subscribe")} />
						</Row>

						{isSubscriptionExpired(props.profile) && isEmptyObject(subscriptionDetails) ? (
							<Row className="mt-4">
								<Col>
									<div className="no-subscription-info p-4 text-center border rounded">
										<p className="mb-2">No active subscription found for your company.</p>
										<p className="text-muted small">
											Please contact{" "}
											<a href="mailto:sales@journeyfoods.com?subject=Missing Subscription Information">
												sales@journeyfoods.com
											</a>{" "}
											if you believe this is a mistake.
										</p>
									</div>
								</Col>
							</Row>
						) : null}

						{!isEmptyObject(subscriptionDetails) && (
							<>
								<Row className={"mt-4 mb-3"}>
									<h3>Itemized Subscription</h3>
									<div style={{ marginLeft: "auto", marginRight: 0, justifySelf: "flex-end" }}>
										<Upgrade />
									</div>
								</Row>
								<Row>
									<Table className={"clean-table"}>
										<thead>
											<tr>
												<th> Service </th>
												<th> Type </th>
												<th> Description </th>
												<th> Status </th>
												<th> Price </th>
												<th></th>
											</tr>
										</thead>
										{subscriptionDetails && (
											<tbody>
												<tr>
													<td> {toCamelCase(subscriptionType || "Free")} Subscription </td>
													<td> Subscription </td>
													<td> {getSubscriptionDescription()}</td>
													<td>
														{" "}
														<span>Paid</span>
													</td>
													<td> {getPrice()} </td>
													<td></td>
												</tr>
											</tbody>
										)}
									</Table>
									{subscriptionDetails && (
										<InvoiceTotal
											subTotal={subscriptionDetails?.latest_invoice?.subtotal}
											total={subscriptionDetails?.latest_invoice?.total}
											tax={subscriptionDetails?.latest_invoice?.tax}
											discount={subscriptionDetails?.latest_invoice?.discount?.coupon?.percent_off}
										/>
									)}
								</Row>
							</>
						)}
						{selectedPlan && (
							<Modal isOpen={checkoutModal}>
								<ModalHeader>
									<div className={"modal-header-content"}>
										<span>Update Your Subscription</span>
										<X
											onClick={() => setCheckoutModal(false)}
											className={"close-modal"}
											size={24}
										></X>
									</div>
								</ModalHeader>
								<ModalBody>
									<SubscriptionCheckoutModal
										active={props.active}
										subscriptionPlan={selectedPlan}
									/>
								</ModalBody>
							</Modal>
						)}
					</Col>
				</>
			)}
		</>
	);
};
const mapStateToProps = (state: IApplicationState) => ({
	profile: state.user.profile,
	credits: state.user.credits,
	permissions: state.user.permissions,
	loadingCredits: state.user.loadingCredits,
	loadingPermissions: state.user.loadingPermissions
});

const mapDispatchToProps = {
	getUserCredits: userService.getUserCredits,
	getUserAccessPermissions: userService.getUserAccessPermissions
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Subscription));
